import React, { useCallback, useMemo, useState } from 'react';
import axios from 'axios';
import { useSearchParams } from 'react-router-dom';
import { useDebouncedSnackbar } from '@client/hooks/use-snackbar.hooks';
import {
  getReportPageKey,
  Report,
  type ReportPageVisibility,
} from '@ui/Report';
import { useResolvedProjectTarget } from '@features/scope/scope-targets.hooks';
import {
  useGetProposalColor,
  useProposalsResultsMaxValue,
} from '@features/proposals/use-proposals.hooks';
import { useProposals } from '@features/proposals/hooks/use-proposals.hooks';
import {
  useFullOrganizationByName,
  useSelectedOrganizationName,
} from '@client/store/organization';
import { getProject, useProjectBuildingGFA } from '@client/store/project';
import { getScope } from '@features/scope/scope.store';
import { useSelectedVersion } from '@client/store/ui';
import { toScopeQueryRecord } from '@shared/helpers/scope-query-params.helpers';
import {
  parseReportPdfOrganizationName,
  REPORT_PDF_ORGANIZATION_NAME_PARAM,
} from '@shared/helpers/report-pdf-generation.helpers';
import amplitudeLog from '@client/amplitude';
import { AMP_REPORT_EXPORT_PDF } from '@shared/constants';
import { ReportCoverPage } from './components/ReportCoverPage';
import {
  ReportCoverImageProvider,
  useReportCoverImage,
} from './report-cover-image.context';
import { ReportCoverImageController } from './components/ReportCoverImageController';
import { ReportSummary } from './components/ReportSummary';
// `ReportElements` (standalone SBEF page) is intentionally not rendered any
// more — the SBEF rows now live inside the per-proposal dashboard
// (`ReportProductGroups`). The component file is kept around in case we want
// to reintroduce a dedicated page later.
import { ReportOperationalEnergy } from './components/ReportOperationalEnergy';
import { ReportProductGroups } from './components/ReportProductGroups/ReportProductGroups';
import { useGetReportProductGroupsDataByProposal } from './components/ReportProductGroups/use-report-product-groups-data.hook';
import { ReportTopProducts } from './components/ReportTopProducts/ReportTopProducts';
import { useGetReportTopProductsByProposal } from './components/ReportTopProducts/use-report-top-products-data.hook';
import { getProposalReportPageSpecs } from './report-proposal-pages.helpers';
import { ReportMethodology } from './components/ReportMethodology/ReportMethodology';
import { buildSbefSegments } from './components/ReportProductGroups/sbef-segments.helpers';
import { useGetSBEFGroupResultsByProposal } from './hooks/use-report-elements';
import { ReportViewFooter } from './components/ReportViewFooter';
import { isReportSlideHidden } from './report-slide-visibility.helpers';
import {
  useSetReportSlideHidden,
  type ReportPageEntry,
} from './hooks/use-report-slide-visibility.hook';
import type {
  ReportProposalSlideKind,
  ReportSharedSlideKind,
} from '@shared/models/report-slides.model';
import { buildReportThumbnailContentKey } from './report-thumbnail-content-key.helpers';
import { MainLayout } from '@ui/layout';
import { useGetOperationalEnergyByProposal } from './components/ReportOperationalEnergy/hooks/use-operational-energy.hook';
import { proposalHasEnergyConsumption } from '@shared/helpers/energy/energy.helpers';
import { DEFAULT_SBEF_CUSTOM_GROUPS } from '@features/sbef/custom-groups.model';
import { assertUnreachable } from '@shared/helpers/function.helpers';
import { guards } from '@shared/helpers/typeguards.helpers';
import { getElementName } from '@shared/helpers/element.helpers';
import { BASELINE_COLOR } from '@shared/helpers/proposals/proposal-colors.constants';
import type { IProposal } from '@shared/models/proposals.model';
import { usePageNavigationHotkeys } from '@client/hooks/use-page-navigation-hotkeys.hook';
import { useIsReadonly } from '@client/hooks/use-user.hooks';
import {
  getReportFooterContextName,
  resolveReportFooterOrganizationName,
} from './report-footer.helpers';
import { buildReportPdfExportBody } from './report-pdf-export.helpers';

type ReportViewProps = {
  hideToolbar?: boolean;
};

type ReportDocumentProps = {
  hideToolbar?: boolean;
  footer: React.ReactNode;
  pages: React.ReactNode[];
  projectId: number;
  projectName: string | undefined;
  /** Baseline open in the report. Print opens this version, not the active one. */
  versionId?: string;
  /** Footer organization label replayed on the print URL. */
  organizationName?: string;
  /** Thumbnail eye toggles — hidden pages leave the canvas, fullscreen and PDF. */
  pageVisibility: ReportPageVisibility;
  thumbnailContentKey?: string;
};

/**
 * Report canvas plus PDF export. Lives under {@link ReportCoverImageProvider}
 * so the live cover map can be snapshotted in the user's browser before
 * Browserless loads PrintView (WebGL does not print in headless Chrome).
 */
const ReportDocument: React.FC<ReportDocumentProps> = ({
  hideToolbar,
  footer,
  pages,
  projectId,
  projectName,
  versionId,
  organizationName,
  pageVisibility,
  thumbnailContentKey,
}) => {
  const [isExporting, setIsExporting] = useState(false);
  const enqueueSnackbar = useDebouncedSnackbar();
  const { captureCoverMapForPdf } = useReportCoverImage();

  const handleExportPdf = useCallback(async () => {
    amplitudeLog(AMP_REPORT_EXPORT_PDF);
    setIsExporting(true);
    try {
      const coverMapDataUrl = await captureCoverMapForPdf();
      const { data } = await axios.post<Blob>(
        `projects/${projectId}/pdf`,
        buildReportPdfExportBody({
          versionId,
          organizationName,
          coverMapDataUrl,
        }),
        {
          responseType: 'blob',
          // PrintView has no user localStorage; replay the current scope on the print URL.
          params: toScopeQueryRecord(getScope()),
        },
      );
      const url = URL.createObjectURL(data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${projectName ?? 'report'}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      enqueueSnackbar('PDF exported', { variant: 'success' });
    } catch {
      enqueueSnackbar('Could not export PDF', { variant: 'error' });
    } finally {
      setIsExporting(false);
    }
  }, [
    captureCoverMapForPdf,
    enqueueSnackbar,
    organizationName,
    projectId,
    projectName,
    versionId,
  ]);

  return (
    <>
      {!hideToolbar && <ReportCoverImageController />}
      <Report
        aspectRatio="16/9"
        scalingStrategy="container"
        footer={footer}
        hideToolbar={hideToolbar}
        onExportPdf={hideToolbar ? undefined : handleExportPdf}
        isExporting={isExporting}
        pageVisibility={pageVisibility}
        thumbnailContentKey={thumbnailContentKey}
      >
        {pages}
      </Report>
    </>
  );
};

export const ReportView: React.FC<ReportViewProps> = ({ hideToolbar }) => {
  usePageNavigationHotkeys(['d', 'b']);
  const getOperationalEnergyByProposal = useGetOperationalEnergyByProposal();
  const getProductGroupsDataByProposal =
    useGetReportProductGroupsDataByProposal();
  const getTopProductsByProposal = useGetReportTopProductsByProposal();
  const getSBEFGroupResultsByProposal = useGetSBEFGroupResultsByProposal({
    customGroups: DEFAULT_SBEF_CUSTOM_GROUPS,
  });
  const project = getProject();
  const selectedVersion = useSelectedVersion();
  const actionProposals = useProposals();

  // In proposal mode, render baseline as its own page first followed by the
  // proposals. Baseline is modeled as a synthetic proposal whose id is the
  // version id so downstream lookups (results record, resolved version) all
  // resolve to the unmodified baseline.
  const proposals = useMemo(() => {
    if (!selectedVersion) return actionProposals;
    const baselineProposal: IProposal = {
      id: selectedVersion.id,
      name: getElementName(selectedVersion),
      colorIndex: -1,
      actionIds: [],
      resultsRecord: selectedVersion.resultsRecord ?? {},
    };
    return [baselineProposal, ...actionProposals];
  }, [actionProposals, selectedVersion]);
  // The report shows the target of the scope the results are reported in.
  const target = useResolvedProjectTarget();
  const { co2ePerGfaMax: proposalsMaxCo2e } = useProposalsResultsMaxValue({
    includeProjectTargetInCo2Scale: true,
  });
  const getProposalColor = useGetProposalColor();

  const selectedOrganization = useSelectedOrganizationName();
  const fullOrganization = useFullOrganizationByName(selectedOrganization);
  const gfa = useProjectBuildingGFA();
  const [searchParams] = useSearchParams();
  // Shared and generation sessions have no Auth0 profile. The URL label
  // fills the footer there; a signed-in display name still wins.
  const printOrganizationName = parseReportPdfOrganizationName(
    searchParams.get(REPORT_PDF_ORGANIZATION_NAME_PARAM),
  );

  const organizationName = resolveReportFooterOrganizationName({
    displayName: fullOrganization?.display_name,
    selectedOrganization,
    projectOrganization: project.organizations?.[0],
    printOrganizationName,
  });
  const footerContextName = getReportFooterContextName(
    selectedVersion ? getElementName(selectedVersion) : undefined,
  );

  const makeFooter = useCallback(
    (contextName: string | undefined) => (
      <ReportViewFooter
        organizationName={organizationName}
        projectName={project?.name}
        contextName={contextName}
        gfa={gfa}
      />
    ),
    [gfa, organizationName, project?.name],
  );

  const footer = makeFooter(footerContextName);

  // Slides toggled off still render a thumbnail, so every page is built here
  // and `isHidden` alone decides whether it reaches the canvas, fullscreen and
  // the PDF export.
  const proposalPageEntries = useMemo<ReportPageEntry[]>(() => {
    if (!selectedVersion) return [];

    const entries = proposals.flatMap((proposal) => {
      // Baseline (synthetic proposal whose id == version.id) has no
      // colorIndex in the proposals list, fall back to the baseline color.
      const isBaseline = proposal.id === selectedVersion.id;
      const color = isBaseline ? BASELINE_COLOR : getProposalColor(proposal);
      const energyData = getOperationalEnergyByProposal(proposal.id);
      const hasEnergyConsumption = proposalHasEnergyConsumption(
        selectedVersion,
        proposal,
      );
      const productGroupsData = getProductGroupsDataByProposal(proposal.id);
      const topProductsData = getTopProductsByProposal(proposal.id);
      // Proposal bar split per SBEF group: widths from the baseline split,
      // heights from proposal-vs-baseline per group. Baseline page passes
      // baseline as both args so bar and fill are equal (no offset shown).
      const baselineGroups = getSBEFGroupResultsByProposal(selectedVersion.id);
      const sbefSegments = buildSbefSegments(
        baselineGroups,
        isBaseline
          ? baselineGroups
          : getSBEFGroupResultsByProposal(proposal.id),
      );
      const proposalName = proposal.name;
      const pageFooter = makeFooter(footerContextName);
      const hiddenSlides = isBaseline
        ? selectedVersion.report_hidden_slides
        : proposal.report_hidden_slides;
      const pageSpecs = getProposalReportPageSpecs({
        hasProductGroupsData: productGroupsData != null,
        hasTopProductsData: topProductsData != null,
        hasEnergyConsumption,
      });
      // Baseline slides live on the version, the rest on their own proposal.
      const toEntry = (
        kind: ReportProposalSlideKind,
        node: React.ReactElement,
      ): ReportPageEntry => ({
        // Every page below is keyed explicitly, so the positional fallback never applies.
        key: getReportPageKey(node, 0),
        node,
        slide: isBaseline
          ? { scope: 'version', kind }
          : { scope: 'proposal', proposalId: proposal.id, kind },
        isHidden: isReportSlideHidden(hiddenSlides, kind),
      });

      return pageSpecs.map((spec) => {
        switch (spec.kind) {
          case 'productGroups':
            if (!productGroupsData) return null;
            return toEntry(
              'productGroups',
              <ReportProductGroups
                key={`${proposal.id}-product-groups`}
                proposalId={proposal.id}
                defaultTitle={proposalName}
                data={productGroupsData}
                isBaseline={isBaseline}
                color={color}
                target={target}
                co2ePerGfaMax={proposalsMaxCo2e}
                sbefSegments={sbefSegments}
                footer={pageFooter}
              />,
            );
          case 'topProducts':
            if (!topProductsData) return null;
            return toEntry(
              'topProducts',
              <ReportTopProducts
                key={`${proposal.id}-top-products`}
                proposalId={proposal.id}
                defaultTitle="High-carbon products"
                color={color}
                isBaseline={isBaseline}
                data={topProductsData}
                footer={pageFooter}
              />,
            );
          case 'energy':
            return toEntry(
              'energy',
              <ReportOperationalEnergy
                key={`${proposal.id}-operational-energy`}
                gfa={gfa}
                color={color}
                energyData={energyData}
                footer={pageFooter}
              />,
            );
          default:
            return assertUnreachable(spec);
        }
      });
    });
    return entries.filter(guards.is.defined);
  }, [
    proposals,
    gfa,
    target,
    proposalsMaxCo2e,
    getOperationalEnergyByProposal,
    getProposalColor,
    getProductGroupsDataByProposal,
    getTopProductsByProposal,
    getSBEFGroupResultsByProposal,
    selectedVersion,
    makeFooter,
    footerContextName,
  ]);

  const hiddenSharedSlides = selectedVersion?.report_hidden_slides;
  const pageEntries = useMemo<ReportPageEntry[]>(() => {
    const toSharedEntry = (
      kind: ReportSharedSlideKind,
      node: React.ReactElement,
    ): ReportPageEntry => ({
      // Keyed explicitly below — the positional fallback never applies.
      key: getReportPageKey(node, 0),
      node,
      slide: { scope: 'version', kind },
      isHidden: isReportSlideHidden(hiddenSharedSlides, kind),
    });

    return [
      toSharedEntry(
        'cover',
        <ReportCoverPage key="report-cover" title={project.name} gfa={gfa} />,
      ),
      toSharedEntry(
        'summary',
        <ReportSummary
          key="report-summary"
          proposals={proposals}
          maxCo2e={proposalsMaxCo2e}
          target={target}
        />,
      ),
      ...proposalPageEntries,
      toSharedEntry(
        'methodology',
        <ReportMethodology key="report-methodology" />,
      ),
    ];
  }, [
    gfa,
    hiddenSharedSlides,
    project.name,
    proposalPageEntries,
    proposals,
    proposalsMaxCo2e,
    target,
  ]);

  const pages = useMemo(
    () => pageEntries.map((entry) => entry.node),
    [pageEntries],
  );

  const isReadonly = useIsReadonly();
  const setReportSlideHidden = useSetReportSlideHidden();
  const pageVisibility = useMemo<ReportPageVisibility>(() => {
    const hiddenPageKeys = pageEntries
      .filter((entry) => entry.isHidden)
      .map((entry) => entry.key);

    if (isReadonly) {
      return { hiddenPageKeys };
    }

    return {
      hiddenPageKeys,
      onTogglePageHidden: (pageKey, nextHidden) => {
        const entry = pageEntries.find((item) => item.key === pageKey);
        if (!entry) return;
        setReportSlideHidden(entry.slide, nextHidden);
      },
    };
  }, [isReadonly, pageEntries, setReportSlideHidden]);

  const thumbnailContentKey = useMemo(
    () =>
      buildReportThumbnailContentKey({
        projectId: project.id,
        projectUpdatedAt: project.updated_at,
        versionId: selectedVersion?.id,
        coverImageUrl: project.cover_image_url,
        pageCount: pages.length,
        gfa,
        target,
        hiddenSharedSlides,
        hiddenPageKeys: pageVisibility.hiddenPageKeys,
        proposals,
      }),
    [
      gfa,
      hiddenSharedSlides,
      pageVisibility.hiddenPageKeys,
      pages.length,
      project.cover_image_url,
      project.id,
      project.updated_at,
      proposals,
      selectedVersion?.id,
      target,
    ],
  );

  return (
    <MainLayout appBarSpace={hideToolbar ? 'none' : 'offset'}>
      <MainLayout.Content>
        <ReportCoverImageProvider projectId={project.id}>
          <ReportDocument
            hideToolbar={hideToolbar}
            footer={footer}
            projectId={project.id}
            projectName={project.name}
            versionId={selectedVersion?.id}
            organizationName={organizationName}
            pageVisibility={pageVisibility}
            pages={pages}
            thumbnailContentKey={thumbnailContentKey}
          />
        </ReportCoverImageProvider>
      </MainLayout.Content>
    </MainLayout>
  );
};
